import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { decryptData } from '../lib/secure-storage';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error', error, errorInfo);

    // Auto-recovery for dynamic module loading failures (e.g. Vite container build restarts or network dropouts)
    try {
      const errorMsg = error ? String(error.message || error) : '';
      if (
        errorMsg.includes('dynamically imported module') ||
        errorMsg.includes('Loading chunk') ||
        errorMsg.includes('Failed to fetch dynamically')
      ) {
        console.warn('[ErrorBoundary Auto-Recovery] Dynamic chunk loading error caught. Auto-reloading page...', error);
        
        // Anti-infinite-loop guard: Only reload if we haven't reloaded in the last 15 seconds
        const lastReload = localStorage.getItem('last-chunk-auto-reload');
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
          localStorage.setItem('last-chunk-auto-reload', String(now));
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('[ErrorBoundary Auto-Recovery Exception]', err);
    }
  }

  render() {
    if (this.state.hasError) {
      let caregiver = 'Mama';
      try {
        const stored = localStorage.getItem('satu-wajan-storage');
        if (stored) {
          const decrypted = decryptData(stored);
          if (decrypted) {
            const parsed = JSON.parse(decrypted);
            if (parsed?.state?.babyProfile?.caregiverName) {
              caregiver = parsed.state.babyProfile.caregiverName;
            }
          }
        }
      } catch (e) {
        // ignore
      }

      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-stone-50 text-center space-y-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-red-600">
            <AlertCircle className="w-10 h-10" />
          </div>
          <div className="space-y-2">
            <h1 className="text-xl font-bold text-stone-900">Aduh, Ada Masalah Teknis!</h1>
            <p className="text-sm text-stone-500 max-w-xs mx-auto">
              Maaf ya {caregiver}, aplikasi Satu Wajan sedang mengalami sedikit gangguan.
            </p>
          </div>
          <Button 
            onClick={() => window.location.reload()}
            className="h-12 px-6 bg-stone-900 text-white rounded-xl font-bold flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Coba Segarkan Halaman
          </Button>
          {import.meta.env.DEV && (
            <pre className="text-[10px] text-red-400 mt-8 max-w-full overflow-auto p-4 bg-white border border-red-100 rounded-lg text-left">
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
